/**
 * DmSearchBar — autocomplete search for the DM inbox. Combines
 * existing conversations that match the query with suggested new members
 * you could start a chat with. Debounced (250ms). Results come from
 * GET /api/dm/search?q=…
 *
 * Props:
 *   onExistingSelect(conversation_id): navigate to existing thread
 *   onNewSelect(peer_id): start a new chat with this member
 */
import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Input } from './ui/input';
import { Search, MessageCircle, UserPlus, Loader2 } from 'lucide-react';

const DmSearchBar = ({ onExistingSelect, onNewSelect }) => {
  const { API } = useContext(AuthContext);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!q.trim() || q.trim().length < 2) { setExisting([]); setSuggested([]); return undefined; }
    const h = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await axios.get(`${API}/dm/search`, { params: { q: q.trim() } });
        setExisting(data?.existing_conversations || []);
        setSuggested(data?.suggested_members || []);
      } catch { /* silent */ }
      finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(h);
  }, [q, API]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pickExisting = (convId) => { setOpen(false); setQ(''); onExistingSelect && onExistingSelect(convId); };
  const pickNew = (peerId) => { setOpen(false); setQ(''); onNewSelect && onNewSelect(peerId); };

  return (
    <div ref={containerRef} className="relative" data-testid="dm-search-bar">
      <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search people…"
        data-testid="dm-search-input"
        className="pl-8 h-9 text-sm"
      />
      {open && q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-full mt-1 rounded-xl border border-border bg-popover shadow-lg z-30 overflow-hidden max-h-80 overflow-y-auto" data-testid="dm-search-results">
          {busy && (
            <div className="px-3 py-2 text-xs text-muted-foreground inline-flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> searching…
            </div>
          )}
          {!busy && existing.length === 0 && suggested.length === 0 && (
            <div className="px-3 py-4 text-xs text-muted-foreground text-center">No matches</div>
          )}
          {existing.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Existing chats</div>
              {existing.map((row) => (
                <button
                  key={row.conversation_id}
                  type="button"
                  onClick={() => pickExisting(row.conversation_id)}
                  className="w-full flex items-start gap-2 px-3 py-2 hover:bg-muted text-left"
                  data-testid={`dm-search-existing-${row.conversation_id}`}
                >
                  <div className="h-8 w-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10px] font-bold flex-shrink-0 overflow-hidden">
                    {row.peer?.avatar_url
                      ? <img src={row.peer.avatar_url} alt="" className="h-full w-full object-cover" />
                      : (row.peer?.name || '?').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold truncate">{row.peer?.name}</span>
                      {row.unread && <span className="h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />}
                    </div>
                    {row.last_snippet && <div className="text-[11px] text-muted-foreground truncate">{row.last_snippet}</div>}
                  </div>
                  <MessageCircle className="w-3 h-3 text-primary flex-shrink-0 mt-1" />
                </button>
              ))}
            </>
          )}
          {suggested.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest font-bold text-muted-foreground border-t border-border">Start a new chat</div>
              {suggested.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => pickNew(p.id)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted text-left"
                  data-testid={`dm-search-suggested-${p.id}`}
                >
                  <div className="h-8 w-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10px] font-bold flex-shrink-0 overflow-hidden">
                    {p.avatar_url
                      ? <img src={p.avatar_url} alt="" className="h-full w-full object-cover" />
                      : (p.name || '?').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold truncate">{p.name}</div>
                    {p.headline && <div className="text-[11px] text-muted-foreground truncate">{p.headline}</div>}
                  </div>
                  <UserPlus className="w-3 h-3 text-primary flex-shrink-0" />
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DmSearchBar;
